import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { z } from 'zod';
import { ConfirmModal } from '../ui/ConfirmModal.js';
import { formatDay } from '../ui/format.js';
import { answeredQuestions } from './answers.js';

const profileSchema = z.object({
  name: z.string(),
  email: z.string(),
  phone: z.string().nullable(),
  submittedAt: z.string(),
  complete: z.boolean(),
  answers: z.record(z.string(), z.union([z.string(), z.array(z.string())])),
  cvFileName: z.string().nullable(),
});

const refusal = z.object({ error: z.string() });

/** The api answers 404 alike for a token nobody holds and a profile already removed. */
class InvalidLink extends Error {}

async function failure(response: Response): Promise<Error> {
  if (response.status === 404) return new InvalidLink();
  const body = refusal.safeParse(await response.json().catch(() => null));
  return new Error(body.success ? body.data.error : `That failed (${response.status}).`);
}

/** The token goes in the body: a path would put it in every request log on the way. */
function manageRequest(path: '' | '/withdraw', token: string): Promise<Response> {
  return fetch(`/talent-api/manage${path}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ token }),
  });
}

async function loadProfile(token: string): Promise<z.infer<typeof profileSchema>> {
  const response = await manageRequest('', token);
  if (!response.ok) throw await failure(response);
  return profileSchema.parse(await response.json());
}

async function withdraw(token: string): Promise<void> {
  const response = await manageRequest('/withdraw', token);
  if (!response.ok) throw await failure(response);
}

/**
 * The token from the manage link, `/manage#<token>`: in the fragment, which the
 * browser never sends, so no server or log on the way sees it. Empty when the
 * link lost its fragment or it is not valid percent-encoding; the api answers
 * either as an unknown link.
 */
function tokenFromFragment(): string {
  try {
    return decodeURIComponent(window.location.hash.slice(1));
  } catch {
    return '';
  }
}

/** A candidate's own profile, reached by the link they were shown once; no session. */
export function ManagePage(): React.JSX.Element {
  const [token] = useState(tokenFromFragment);
  const [confirming, setConfirming] = useState(false);
  const profile = useQuery({
    queryKey: ['talent-manage', token],
    queryFn: () => loadProfile(token),
    retry: false,
  });
  const remove = useMutation({
    mutationFn: () => withdraw(token),
    onSettled: () => setConfirming(false),
  });

  const person = profile.data;
  const invalid = profile.error instanceof InvalidLink || remove.error instanceof InvalidLink;

  return (
    <div className="signin apply">
      <div className="signin-card">
        <h1>Talent Pool</h1>
        {remove.isSuccess ? (
          <p>Removed. Your profile and CV are gone, and recruiters can no longer find you.</p>
        ) : invalid ? (
          <p>This link is not valid.</p>
        ) : profile.isPending ? (
          <p className="subtle">…</p>
        ) : person === undefined ? (
          <p className="signin-error">Could not load your profile. Try the link again later.</p>
        ) : (
          <>
            <p className="subtle">
              {person.complete
                ? 'Your profile, as recruiters see it beside your CV.'
                : 'Your profile. Recruiters will not see it until every required question is answered.'}
            </p>
            <dl className="pane-facts">
              <Fact label="Name" value={person.name} />
              <Fact label="Email" value={person.email} />
              <Fact label="Phone" value={person.phone} />
              <Fact label="Submitted" value={formatDay(person.submittedAt)} />
              <Fact label="CV" value={person.cvFileName} />
              {answeredQuestions(person.answers).map((answer) => (
                <Fact key={answer.id} label={answer.label} value={answer.text} />
              ))}
            </dl>
            <button
              className="button danger"
              type="button"
              disabled={remove.isPending}
              onClick={() => setConfirming(true)}
            >
              Remove my profile
            </button>
            {remove.isError && <p className="signin-error">{remove.error.message}</p>}
          </>
        )}
      </div>
      {confirming && (
        <ConfirmModal
          title="Remove your profile and CV?"
          body="Recruiters will no longer find you. This cannot be undone; to come back, send the form again."
          confirmLabel="Remove"
          danger
          onConfirm={() => remove.mutate()}
          onCancel={() => setConfirming(false)}
        />
      )}
    </div>
  );
}

function Fact(props: { label: string; value: string | null }): React.JSX.Element | null {
  if (props.value === null || props.value === '') return null;
  return (
    <div className="pane-fact">
      <dt className="pane-fact-label">{props.label}</dt>
      <dd className="pane-fact-value">{props.value}</dd>
    </div>
  );
}
